import { groupBy, sortBy, transform } from 'lodash';

import type {
  SearchableSelectConfig,
  SearchableSelectOption
} from '@/components/SearchableSelect';

import type { SearchFilters } from './SearchBar.types';

// A helper function to get the searchable select options for a given field based on the rows data
export const getSearchableSelectOptions = <T>(
  rows: T[],
  field: string,
  title: string,
  options: SearchableSelectOption[] = []
): SearchableSelectOption[] => {
  const valueLabels: Record<string, string> = transform(
    options,
    (result: Record<string, string>, option) => {
      result[String(option.value)] = option.label;
    },
    {}
  );

  const filterValues = groupBy(rows, field);

  const filterOptions = Object.entries(filterValues).map(([key, value]) => {
    let filterValue = key;

    let label = key === '' ? `No ${title}` : key;
    if (valueLabels[key]) {
      label = valueLabels[key];
    }
    // This addresses cases where the value is 'null' and should be displayed as 'No {title}' such as in Assets page where fields come over as null often
    if (label === 'null') {
      label = `No ${title}`;
      filterValue = '';
    }
    return {
      value: filterValue,
      label,
      count: value.length
    };
  });

  return sortBy(filterOptions, 'value');
};

const getFilterOptionLabel = (value: string, title: string) => {
  if (value === '') {
    return `No ${title}`;
  }
  return value;
};

// A helper function to add zero count options to the filter options
// Used to add options that would normally be filtered out due to having a count of 0, but should still appear because they are selected filters
// Returns filter options with zero count options added
export const addZeroCountOptions = (
  filter: SearchableSelectConfig,
  currentFilters: SearchFilters,
  options: SearchableSelectOption[],
  labelFormatter?: (label: string) => string
) => {
  const filterField = filter.field;
  const filterValues = currentFilters[filterField];
  filterValues.forEach((value) => {
    options.push({
      value,
      label: labelFormatter
        ? labelFormatter(value)
        : getFilterOptionLabel(value, filter.title), // if the value is empty, set it to an empty string to make sure it will show up appropriately in the dropdown and search bar
      count: 0
    });
  });
  return options;
};

export const mergeSearchableSelectFieldOptions = (
  filterOptions: SearchableSelectOption[],
  additionalOptions: SearchableSelectOption[]
) => {
  // Combine options keyed by `value`. When an option exists in both arrays, use the non-zero
  // count from `additionalOptions`; if that count is zero, preserve the existing count from
  // `filterOptions`. Options that exist only in `additionalOptions` are added to the result.
  const optionsMap: Record<string, SearchableSelectOption> = {};
  filterOptions.forEach((option) => {
    optionsMap[String(option.value)] = { ...option };
  });
  additionalOptions.forEach((option) => {
    const optionKey = String(option.value);
    if (optionsMap[optionKey]) {
      if (option.count !== 0) {
        optionsMap[optionKey].count = option.count;
      }
    } else {
      optionsMap[optionKey] = { ...option };
    }
  });

  return sortBy(Object.values(optionsMap), 'label');
};

export const mergeSearchableSelectOptions = (
  options1: Record<string, SearchableSelectOption[]>,
  options2: Record<string, SearchableSelectOption[]>
): Record<string, SearchableSelectOption[]> => {
  const options = {} as Record<string, SearchableSelectOption[]>;
  Object.entries(options1 || {}).forEach(([key, value]) => {
    options[key] = mergeSearchableSelectFieldOptions(
      value as SearchableSelectOption[],
      options2?.[key] ? (options2[key] as SearchableSelectOption[]) : []
    );
  });
  // add any missing options from options2 that are not in ticketEntities
  Object.entries(options2 || {}).forEach(([key, value]) => {
    if (!options[key]) {
      options[key] = value as SearchableSelectOption[];
    }
  });

  return options;
};

export default getSearchableSelectOptions;
